import { Component, Suspense, lazy, useCallback, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { layerById, layerIdsFor, chipLabel, swatchBackground, titleCaseName, CHIP_LAYERS, CHIP_TEXT, COAL_DATASET, MINE_ENTRIES_NOTE } from './locateLayers';
import { refreshConstraints, type BusFigures, type SiteConstraint, type TransportPlace } from '../api/locate';
import type { MapFocus, MapMarker, ViewArea } from './LocateMap';

const LocateMap = lazy(() => import('./LocateMap'));

export class MapBoundary extends Component<{ children: ReactNode; failMessage?: string }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    return this.state.failed
      ? <div className="lm-map lm-map-off">{this.props.failMessage ?? 'The map could not be loaded.'}</div>
      : this.props.children;
  }
}

const DETAIL_NAMES = 8;

/** One box under the chips holding everything to read about each switched-on layer: the names found, the colour key (only grades in the current view) and any hints. */
export function MapKey({ active, constraints, view, grades, notes, transport, onFocus, focusId, part = 'site' }: { part?: 'site' | 'view'; active: string[]; constraints: SiteConstraint[]; view?: ViewDesignations; grades: Record<string, string[] | null>; notes: Record<string, string>; transport?: { constraints: SiteConstraint[]; on: string[] }; onFocus?: (m: MapMarker) => void; focusId?: string | null }) {
  const siteChips = presentChips(constraints);
  const chips = [...siteChips, ...presentChips(view?.extra ?? [])];
  const defs = active.map(id => layerById(id)).filter((d): d is NonNullable<ReturnType<typeof layerById>> => !!d);
  const rows = defs.map(d => {
    const chip = chips.find(c => (CHIP_LAYERS[c.dataset] ?? [c.dataset]).includes(d.id));
    const keyed = d.legend.length > 1;
    const inView = grades[d.id];
    const graded = d.kind === 'geojson' && !!d.colourField;
    const items = !keyed ? [] : graded ? (inView ? d.legend.filter(item => item.grade !== undefined && inView.includes(item.grade)) : []) : d.legend;
    const listable = !!chip && LISTS_FEATURES.has(chip.dataset);
    const siteChip = chip && siteChips.includes(chip) ? chip : undefined;
    const places = listable && chip ? featureRows(chip) : [];
    const rerun = listable && siteChip && places.length === 0 && siteChip.count > 0;
    const nameList = !keyed && chip && chip.dataset !== 'mine-entries' && places.length === 0 ? chip.names.slice(0, DETAIL_NAMES) : [];
    const more = chip && nameList.length > 0 && chip.count > nameList.length ? `and ${chip.count - nameList.length} more` : '';
    const partial = chip && places.length > 0 && chip.count > places.length ? `Listing ${places.length} of ${chip.count}.` : '';
    const edge = siteChip?.edgeOnly ? (typeof siteChip.coveragePct === 'number'
      ? `Edge of site: only about ${siteChip.coveragePct.toFixed(1)}% of the traced outline overlaps this, within the placement error of the outline, so the site is not treated as within it unless the planning statement says so.`
      : 'Edge of site: only a sliver at the edge of the search area, not reaching inside the traced outline, so it is left out of the assessment.') : '';
    return {
      d,
      inView: !!chip && !siteChip,
      title: chip ? (CHIP_TEXT[chip.dataset] ?? (chip.label || chip.dataset)) : d.label,
      items,
      places,
      nameList,
      text: [edge, more, partial, rerun ? 'Re-run the location step to list these individually and plot them on the map.' : '', notes[d.id], d.note].filter((t): t is string => !!t),
    };
  }).filter(r => (part === 'view') === r.inView).filter(r => r.items.length > 0 || r.places.length > 0 || r.text.length > 0);
  const transportRows = transport ? TRANSPORT_ORDER.map(d => transport.constraints.find(c => baseOf(c.dataset) === d)).filter((c): c is SiteConstraint => !!c && (c.status === 'Present' || c.status === 'Absent') && transport.on.includes(c.dataset)) : [];
  const nearbyOn = transport ? transport.constraints.filter(c => isNearbyHeritage(c) && c.status === 'Present' && transport.on.includes(c.dataset)) : [];
  if (rows.length === 0 && transportRows.length === 0 && nearbyOn.length === 0) return null;
  return (
    <div className="lm-key lm-chip-details" role="group" aria-label="About the layers switched on">
      {rows.map(({ d, title, items, places, nameList, text }) => (
        <div key={d.id} className="lm-chip-detail">
          <span className="lm-key-title">{title}</span>
          {items.length > 0 && (
            <div className="lm-key-layer">
              {items.map(item => <span key={item.label} className="lm-legend-item"><i style={{ background: item.colour }} />{item.label}</span>)}
            </div>
          )}
          {places.length > 0 && <PlaceList rows={places} onFocus={onFocus} focusId={focusId} />}
          {nameList.length > 0 && <NameList names={nameList} />}
          {text.map(t => <span key={t}>{t}</span>)}
        </div>
      ))}
      {nearbyOn.map(c => <NearbyDetail key={c.dataset} c={c} onFocus={onFocus} focusId={focusId} />)}
      {transportRows.map(c => <TransportDetail key={c.dataset} c={c} onFocus={onFocus} focusId={focusId} />)}
    </div>
  );
}

/** Designations whose individual features are listed (and plotted) once the chip is switched on. */
const AREA_DESIGNATIONS = new Set(['article-4-direction-area', 'air-quality-management-area', 'conservation-area', 'scheduled-monument', 'park-and-garden', 'battlefield', 'archaeological-priority-area', 'tree-preservation-zone', 'ancient-woodland', 'site-of-special-scientific-interest', 'special-area-of-conservation', 'special-protection-area', 'ramsar', 'national-nature-reserve', 'local-nature-reserve', 'green-belt', 'nutrient-neutrality-catchment', 'world-heritage-site', 'world-heritage-site-buffer-zone', 'area-of-outstanding-natural-beauty', 'national-park', 'heritage-coast', 'flood-storage-area', 'registered-common-land']);
const LISTS_FEATURES = new Set(['listed-building-outline', 'locally-listed-building', 'heritage-at-risk', 'brownfield-land', 'asset-of-community-value', 'long-established-woodland', ...AREA_DESIGNATIONS]);

/** distanceM is null for an area designation, whose plotted point is only its middle, so a distance would mislead. */
interface PlaceRow { marker: MapMarker; name: string; distanceM: number | null; freq: string | null }

/** Each individually listed feature of a designation (for example each listed building), nearest first. */
function featureRows(c: SiteConstraint): PlaceRow[] {
  const area = AREA_DESIGNATIONS.has(c.dataset);
  return [...(c.features ?? [])].sort((a, b) => a.distanceM - b.distanceM).map((f, i) => {
    const name = titleCaseName(f.name);
    return { marker: { id: `${c.dataset}:${i}`, lat: f.lat, lon: f.lon, colour: featureColour(c.dataset), label: area ? name : `${name} · ${fmtDistance(f.distanceM)}`, ...(f.detail ? { lines: [f.detail] } : {}) }, name, distanceM: area ? null : f.distanceM, freq: f.detail ?? null };
  });
}

function featureColour(dataset: string): string {
  const def = layerIdsFor(dataset).map(id => layerById(id)).find(d => d && d.kind !== 'raster');
  return def && def.kind !== 'raster' ? def.colour : '#c0392b';
}

/** Map points for the individually listed features of every switched-on designation. */
export function featureMarkers(constraints: SiteConstraint[], active: string[]): MapMarker[] {
  return constraints.filter(c => c.status === 'Present' && LISTS_FEATURES.has(c.dataset) && layerIdsFor(c.dataset).length > 0 && layerIdsFor(c.dataset).every(id => active.includes(id)))
    .flatMap(c => featureRows(c).map(r => r.marker));
}

/** A list of named places; each row zooms the map to its point when clicked. */
function StopIcon({ zoomOut }: { zoomOut: boolean }) {
  return (
    <svg className="lm-stop-icon" viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
      <circle cx="6.5" cy="6.5" r="4.5" />
      <path d="M10 10l4.5 4.5" />
      <path d={zoomOut ? 'M4.5 6.5h4' : 'M4.5 6.5h4M6.5 4.5v4'} />
    </svg>
  );
}

/** Names with no map point, one per row. */
function NameList({ names }: { names: string[] }) {
  return (
    <ul className="lm-stops">
      {names.map(n => <li key={n}><span className="lm-stop-name">{n}</span></li>)}
    </ul>
  );
}

function PlaceList({ rows, onFocus, focusId }: { rows: PlaceRow[]; onFocus?: (m: MapMarker) => void; focusId?: string | null }) {
  return (
    <>
      {onFocus && <span className="lm-stops-hint">Click a row to zoom the map to it.</span>}
      <ul className="lm-stops">
        {rows.map(r => {
          const on = focusId === r.marker.id;
          const name = <span className="lm-stop-name">{r.name}</span>;
          const dist = r.distanceM != null && <span className="lm-stop-dist">{fmtDistance(r.distanceM)}</span>;
          const freq = r.freq && <span className="lm-stop-freq">{r.freq}</span>;
          return (
            <li key={r.marker.id}>
              {onFocus
                ? <button type="button" className={`lm-stop-btn${on ? ' lm-stop-btn-on' : ''}`} title={on ? 'Zoom the map back out.' : 'Zoom the map to this point.'} onClick={() => onFocus(r.marker)}>{name}{dist}<StopIcon zoomOut={on} />{freq}</button>
                : <>{name}{dist}{freq}</>}
            </li>
          );
        })}
      </ul>
    </>
  );
}

/** Data credits for the switched-on layers. */
export function MapCredits({ active }: { active: string[] }) {
  const credits = [...new Set(active.map(id => layerById(id)?.attribution).filter((a): a is string => !!a))];
  return credits.length > 0 ? <p className="sx-note">{credits.join(' · ')}</p> : null;
}

/** Designations that come into view beyond the search area, found by looking up the visible part of the map. */
export interface ViewDesignations {
  onViewChange: (v: ViewArea) => void;
  extra: SiteConstraint[];
  /** Stops and stations in the view that are not already listed for the site, distances measured from the pin. */
  extraTransport: SiteConstraint[];
  loading: boolean;
  error: string | null;
  /** The view is wider than the lookup allows, so only its middle was checked. */
  capped: boolean;
}

const MAX_VIEW_RADIUS_M = 5_000;
const MAX_VIEW_STATION_RADIUS_M = 100_000;
const VIEW_LOOKUP_DELAY_MS = 600;
// The opening frame already shows a little more than the search area, so only a clearly wider or moved view triggers a lookup.
const VIEW_MARGIN = 2;
const MIN_FRAME_RADIUS_M = 400;

function distanceM(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const dLat = (a.lat - b.lat) * 111_320;
  const dLon = (a.lon - b.lon) * 111_320 * Math.cos((a.lat * Math.PI) / 180);
  return Math.hypot(dLat, dLon);
}

/** Looks up designations for the visible map once it shows well beyond the search area, and keeps looking up every view after that (including zoomed right in), so the chips always mean what is on screen but not already listed for the site. Results are display only. */
export function useViewDesignations(caseId: string | undefined, pin: { lat: number; lon: number } | null, radiusM: number, known: SiteConstraint[]): ViewDesignations {
  const [view, setView] = useState<ViewArea | null>(null);
  const [found, setFound] = useState<SiteConstraint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onViewChange = useCallback((v: ViewArea) => setView(v), []);

  const wider = !!view && !!pin && !!caseId && distanceM(view, pin) + view.radiusM > Math.max(radiusM, MIN_FRAME_RADIUS_M) * VIEW_MARGIN;
  const capped = !!view && view.radiusM > MAX_VIEW_RADIUS_M;
  const lat = view ? Math.round(view.lat * 1e4) / 1e4 : 0;
  const lon = view ? Math.round(view.lon * 1e4) / 1e4 : 0;
  const lookup = wider || found.length > 0;
  const lookupRadius = view ? Math.min(MAX_VIEW_STATION_RADIUS_M, Math.max(1, Math.round(view.radiusM))) : 0;

  useEffect(() => { setFound([]); }, [caseId]);
  useEffect(() => {
    if (!lookup || !caseId) {
      setLoading(false); setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true); setError(null);
    const timer = setTimeout(() => {
      refreshConstraints(caseId, lat, lon, lookupRadius, undefined, true)
        .then(list => { if (!cancelled) setFound(list); })
        .catch((e: Error) => { if (!cancelled) setError(e.message); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, VIEW_LOOKUP_DELAY_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [lookup, caseId, lat, lon, lookupRadius]);

  const have = new Set(known.filter(c => c.status === 'Present').map(c => c.dataset));
  // The lookup measures from the middle of the view, so listed features are re-measured from the pin.
  const fromPin = (c: SiteConstraint): SiteConstraint => pin && c.features ? { ...c, features: c.features.map(f => ({ ...f, distanceM: Math.round(distanceM(pin, f)) })) } : c;
  const extra = found.filter(c => c.status === 'Present' && !have.has(c.dataset)).map(fromPin);
  // Transport is not dropped when the map zooms in: whatever the last lookup found stays while it is on screen.
  const onScreen = (p: { lat: number; lon: number }) => !view || distanceM(view, p) <= view.radiusM;
  const extraTransport = pin ? viewTransport(found.map(c => ({ ...c, places: (c.places ?? []).filter(onScreen) })), known, pin) : [];
  return { onViewChange, extra, extraTransport, loading: lookup && loading, error: lookup ? error : null, capped };
}

const isTransport = (c: SiteConstraint) => c.group === 'Transport';

/** Transport found only by looking up the wider view carries this prefix on its dataset, so it never collides with the site's own transport chips. */
const VIEW_PREFIX = 'view:';
/** Heritage assets near, not on, the site (within 500 m): listed with distances like transport, never as on-site designations. */
const NEARBY_PREFIX = 'heritage-nearby-';
const isNearbyHeritage = (c: SiteConstraint) => c.dataset.startsWith(NEARBY_PREFIX);
const NEARBY_COLOUR = '#9a4d1c';
const NEARBY_SHORT: Record<string, string> = {
  'heritage-nearby-listed-building': 'Listed buildings',
  'heritage-nearby-scheduled-monument': 'Scheduled monuments',
  'heritage-nearby-conservation-area': 'Conservation areas',
  'heritage-nearby-park-and-garden': 'Parks and gardens',
  'heritage-nearby-battlefield': 'Battlefields',
  'heritage-nearby-world-heritage-site': 'World Heritage Sites',
};
const NEARBY_ORDER = Object.keys(NEARBY_SHORT);

/** Each nearby asset, nearest first, with its grade where it has one. */
function nearbyRows(c: SiteConstraint): PlaceRow[] {
  return [...(c.features ?? [])].sort((a, b) => a.distanceM - b.distanceM).map((f, i) => {
    const name = titleCaseName(f.name);
    return { marker: { id: `${c.dataset}:${i}`, lat: f.lat, lon: f.lon, colour: NEARBY_COLOUR, label: `${name} · ${fmtDistance(f.distanceM)}`, ...(f.detail ? { lines: [f.detail] } : {}) }, name, distanceM: f.distanceM, freq: f.detail ?? null };
  });
}

function nearbyTitle(c: SiteConstraint): string {
  return `${NEARBY_SHORT[c.dataset] ?? c.label} within 500 m of the ${c.measuredFrom === 'pin' ? 'pin' : 'site'} · ${c.count}`;
}

/** The white panel behind a switched-on nearby-heritage chip. */
function NearbyDetail({ c, onFocus, focusId }: { c: SiteConstraint; onFocus?: (m: MapMarker) => void; focusId?: string | null }) {
  const rows = nearbyRows(c);
  return (
    <div className="lm-chip-detail">
      <span className="lm-key-title">{nearbyTitle(c)}</span>
      {rows.length > 0 ? <PlaceList rows={rows} onFocus={onFocus} focusId={focusId} /> : <NameList names={c.names.slice(0, DETAIL_NAMES)} />}
      {c.count > rows.length && rows.length > 0 && <span>Listing the nearest {rows.length} of {c.count}.</span>}
      <span>Not on the site, but development nearby can affect their setting. Straight-line distances to the nearest part of the {c.measuredFrom === 'pin' ? 'search area round the pin' : 'site outline'}.</span>
    </div>
  );
}

function NearbyButtons({ list, on, onToggle }: { list: SiteConstraint[]; on: string[]; onToggle: (dataset: string, on: boolean) => void }) {
  return (
    <>
      {list.map(c => {
        const isOn = on.includes(c.dataset);
        const names = (c.features?.length ? c.features.map(f => titleCaseName(f.name)) : c.names).slice(0, 4).join('; ');
        const title = `${nearbyTitle(c)}${names ? `: ${names}` : ''}. Click to ${isOn ? 'hide' : 'show'} the details and the map points.`;
        return (
          <button key={c.dataset} type="button" className={`lm-chip lm-chip-btn${isOn ? ' lm-chip-on' : ''}`} aria-pressed={isOn} title={title} onClick={() => onToggle(c.dataset, !isOn)}>
            <i style={{ background: NEARBY_COLOUR }} /><span className="lm-chip-text">{`${NEARBY_SHORT[c.dataset] ?? c.label} nearby (${c.count})`}</span>
          </button>
        );
      })}
    </>
  );
}

const baseOf = (dataset: string) => dataset.startsWith(VIEW_PREFIX) ? dataset.slice(VIEW_PREFIX.length) : dataset;
const placeKey = (p: { name: string; lat: number; lon: number }) => `${p.name}|${p.lat.toFixed(5)}|${p.lon.toFixed(5)}`;

/** The stops and stations a wider view lookup found beyond those already listed for the site, with distances re-measured from the pin (the lookup measures from the middle of the view). */
function viewTransport(found: SiteConstraint[], known: SiteConstraint[], pin: { lat: number; lon: number }): SiteConstraint[] {
  const seen = new Set(known.filter(isTransport).flatMap(c => (c.places ?? []).map(placeKey)));
  const out: SiteConstraint[] = [];
  for (const c of found) {
    if (!isTransport(c) || c.status !== 'Present') continue;
    const places = (c.places ?? []).filter(p => !seen.has(placeKey(p))).map(p => ({ ...p, distanceM: Math.round(distanceM(pin, p)) })).sort((a, b) => (a.distanceM ?? 0) - (b.distanceM ?? 0));
    if (places.length === 0) continue;
    const { within400M: _a, within800M: _b, busService: _c, centreDistanceM: _d, ...rest } = c;
    out.push({ ...rest, dataset: `${VIEW_PREFIX}${c.dataset}`, count: places.length, names: [places[0].name], distanceM: places[0].distanceM, measuredFrom: 'pin', places });
  }
  return out;
}

/** A Present designation as chips (transport has its own chips); the coal high risk area also brings its mine entries chip. */
// Covers essentially all of England, so it says nothing about a site; cases located before it was dropped still carry it.
const RETIRED_DATASET = 'sssi-impact-risk-zone';

function presentChips(list: SiteConstraint[]): SiteConstraint[] {
  return list.filter(c => c.status === 'Present' && !isTransport(c) && !isNearbyHeritage(c) && c.dataset !== RETIRED_DATASET).flatMap((c): SiteConstraint[] => c.dataset === COAL_DATASET
    ? [c, { ...c, dataset: 'mine-entries', label: 'Mine entries (shafts and adits)', count: 0, names: [MINE_ENTRIES_NOTE] }]
    : [c]);
}

function ChipGroup({ list, active, onToggle, label }: { list: SiteConstraint[]; active: string[]; onToggle: (id: string, on: boolean) => void; label: string }) {
  return (
    <div className="lm-chips" role="group" aria-label={label}>
      <DesignationButtons list={list} active={active} onToggle={onToggle} />
    </div>
  );
}

function DesignationButtons({ list, active, onToggle }: { list: SiteConstraint[]; active: string[]; onToggle: (id: string, on: boolean) => void }) {
  const on = new Set(active);
  return (
    <>
      {list.map(c => {
        const ids = (CHIP_LAYERS[c.dataset] ?? [c.dataset]).filter(id => layerById(id));
        const text = `${chipLabel(c.dataset, c.label)}${c.edgeOnly ? ' – edge of site' : c.count > 1 ? ` (${c.count})` : ''}`;
        const full = CHIP_TEXT[c.dataset] ?? (c.label || c.dataset);
        const names = c.names.slice(0, 4).join('; ');
        const title = c.edgeOnly
          ? (typeof c.coveragePct === 'number' ? `${full}: only about ${c.coveragePct.toFixed(1)}% of the site outline overlaps it, within the placement error of the traced outline, so the site is not treated as within it` : `${full}: only a sliver along the edge of the search area, not reaching inside the site outline, so it is left out of the assessment`)
          : names ? `${full}: ${names}${c.count > 4 ? ` and ${c.count - 4} more` : ''}` : full;
        const swatch = ids.length ? swatchBackground(ids.flatMap(id => layerById(id)?.legend ?? [])) : undefined;
        if (!ids.length) return <span key={c.dataset} className={`lm-chip lm-chip-static${c.edgeOnly ? ' lm-chip-edge' : ''}`} title={title}><span className="lm-chip-text">{text}</span></span>;
        const isOn = ids.every(id => on.has(id));
        const action = `Click to ${isOn ? 'hide' : 'show'} on the map.`;
        return (
          <button
            key={c.dataset} type="button" className={`lm-chip lm-chip-btn${isOn ? ' lm-chip-on' : ''}${c.edgeOnly ? ' lm-chip-edge' : ''}`}
            aria-pressed={isOn} title={`${title}. ${action}`}
            onClick={() => ids.forEach(id => onToggle(id, !isOn))}
          >
            {swatch && <i style={{ background: swatch }} />}<span className="lm-chip-text">{text}</span>
          </button>
        );
      })}
    </>
  );
}

/** Designations and public transport found around the site as one wrapping row of chips, so the rows fill evenly; a chip with a matching map layer switches that layer on and off. */
export function SiteChips({ constraints, active, onToggle, transport, disclaimer = true }: { constraints: SiteConstraint[]; active: string[]; onToggle: (id: string, on: boolean) => void; transport: TransportControl; disclaimer?: boolean }) {
  const present = presentChips(constraints);
  const designations = constraints.filter(c => !isTransport(c) && !isNearbyHeritage(c));
  const nearby = NEARBY_ORDER.map(d => constraints.find(c => c.dataset === d)).filter((c): c is SiteConstraint => !!c && c.status === 'Present');
  const unchecked = designations.filter(c => c.status === 'Not checked').length;
  const rows = TRANSPORT_ORDER.map(d => constraints.find(c => c.dataset === d)).filter((c): c is SiteConstraint => !!c);
  // Only stops that were found get a chip; none found shows nothing, and only a check that could not run gets a note.
  const found = rows.filter(c => c.status === 'Present');
  const others = rows.filter(c => c.status === 'Not checked');
  if (designations.length === 0 && rows.length === 0 && nearby.length === 0) return null;
  return (
    <div className="lm-chips-wrap">
      {(present.length > 0 || found.length > 0 || nearby.length > 0) && (
        <div className="lm-chips" role="group" aria-label="Designations, nearby heritage and public transport">
          <DesignationButtons list={present} active={active} onToggle={onToggle} />
          <NearbyButtons list={nearby} on={transport.on} onToggle={transport.onToggle} />
          <TransportButtons list={found} on={transport.on} onToggle={transport.onToggle} />
        </div>
      )}
      {designations.length > 0 && present.length === 0 && <div className="sx-note">No designations were found in the search area.</div>}
      {unchecked > 0 && <div className="sx-note">{unchecked} {unchecked === 1 ? 'designation' : 'designations'} could not be checked.</div>}
      {others.map(c => (
        <div key={c.dataset} className="sx-note">
          {TRANSPORT_NOUN[c.dataset] ?? c.label}: {c.reason ?? 'not checked'}
        </div>
      ))}
      {disclaimer && designations.length > 0 && <DesignationDisclaimer />}
      {disclaimer && rows.length > 0 && <TransportDisclaimer constraints={constraints} />}
    </div>
  );
}

export function DesignationDisclaimer() {
  return <div className="sx-note">Indicative only, from planning.data.gov.uk, Natural England and Mining Remediation Authority open data, within the search area. The coal mining area is a published "subject to change" layer, so take a Coal Authority mining report for any site in or near it. Small designations such as listed buildings and tree preservation orders may be missed or over-reported.</div>;
}

interface TransportControl { on: string[]; onToggle: (dataset: string, on: boolean) => void }

function ViewBusy({ label }: { label: string }) {
  return <span className="lm-chip lm-chip-static lm-chip-busy" role="status"><span className="lm-map-chip-spin" aria-hidden="true" />{label}</span>;
}

/** Designations and public transport that appear only in the part of the map currently on screen, beyond the search area. They come and go as the map is zoomed. `detail` is the white panel for whichever of these chips are switched on, shown beneath them. */
export function ViewChips({ active, onToggle, view, transport, detail }: { active: string[]; onToggle: (id: string, on: boolean) => void; view: ViewDesignations; transport: TransportControl; detail?: ReactNode }) {
  const extra = presentChips(view.extra);
  const any = extra.length > 0 || view.extraTransport.length > 0;
  if (!any && !view.loading && !view.error) return null;
  return (
    <div className="lm-chips-wrap">
      {any && (
        <>
          <div className="lm-view-head">
            <div className="sx-note">Also in the current view{view.capped ? ' (designations are checked within 5 km of the middle of the view, bus stops within 10 km, stations within 100 km; zoom in to see closer)' : ''}</div>
            {view.loading && <ViewBusy label="Updating…" />}
          </div>
          {extra.length > 0 && <ChipGroup list={extra} active={active} onToggle={onToggle} label="Designations found elsewhere in the current map view" />}
          {view.extraTransport.length > 0 && <TransportChipRow list={view.extraTransport} on={transport.on} onToggle={transport.onToggle} label="Public transport elsewhere in the current map view" />}
          {detail}
        </>
      )}
      {!any && view.loading && <div className="lm-view-head"><ViewBusy label="Checking the wider view…" /></div>}
      {!any && !view.loading && view.error && <div className="sx-note">Designations in the wider view could not be checked.</div>}
    </div>
  );
}

const TRANSPORT_COLOUR: Record<string, string> = {
  'transport-bus-stop': '#1d6fb8',
  'transport-rail-station': '#7b2d8e',
  'transport-tram-metro': '#0b8a6a',
};
const TRANSPORT_ORDER = Object.keys(TRANSPORT_COLOUR);
const TRANSPORT_NOUN: Record<string, string> = {
  'transport-bus-stop': 'Nearest bus stop',
  'transport-rail-station': 'Railway station',
  'transport-tram-metro': 'Tram or metro stop',
};

function fmtDistance(m: number): string {
  if (m === 0) return 'on site';
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
}

function busFiguresText(f: BusFigures): string {
  if (f.weekday === 0 && f.saturday === 0 && f.sunday === 0) return 'no scheduled buses';
  return `${f.peakHour} in the busiest hour · ${f.weekday} a weekday · ${f.saturday} Sat · ${f.sunday} Sun · ${f.services} ${f.services === 1 ? 'route' : 'routes'}`;
}

function figuresOrState(f: BusFigures | 'none' | 'unknown'): string {
  return f === 'none' ? 'none scheduled' : f === 'unknown' ? 'frequency unknown' : busFiguresText(f);
}

/** How often buses call at one stop. Cases saved before per-stop figures were recorded fall back to the nearest and best-served stop figures; null when no timetable data was used. */
function stopFrequency(bus: SiteConstraint, p: TransportPlace, index: number): string | null {
  if (p.service != null) return figuresOrState(p.service);
  const summary = bus.busService;
  if (!summary) return null;
  if (index === 0) return figuresOrState(summary.nearest);
  if (summary.best && summary.best.name === p.name && summary.best.distanceM === p.distanceM) return busFiguresText(summary.best);
  return 'frequency not recorded';
}

function stopRows(c: SiteConstraint): PlaceRow[] {
  const base = baseOf(c.dataset);
  const isBus = base === 'transport-bus-stop';
  const fromView = c.dataset !== base;
  return (c.places ?? [])
    .map((p, i): PlaceRow => {
      const freq = isBus ? stopFrequency(c, p, i) : null;
      return { marker: { id: fromView ? `${c.dataset}:${p.lat},${p.lon}` : `${c.dataset}:${i}`, lat: p.lat, lon: p.lon, colour: TRANSPORT_COLOUR[base] ?? '#1d6fb8', label: `${p.name} · ${fmtDistance(p.distanceM)}`, ...(freq ? { lines: [freq] } : {}) }, name: p.name, distanceM: p.distanceM, freq };
    })
    .sort((a, b) => (a.distanceM ?? 0) - (b.distanceM ?? 0));
}

/** The white panel behind a switched-on transport chip: each stop or station by name, nearest first, with how often buses call where that is known. */
function TransportDetail({ c, onFocus, focusId }: { c: SiteConstraint; onFocus?: (m: MapMarker) => void; focusId?: string | null }) {
  if (c.status === 'Absent') {
    return (
      <div className="lm-chip-detail">
        <span className="lm-key-title">{TRANSPORT_NOUN[c.dataset] ?? c.label}</span>
        <span>{c.names[0] ?? 'None recorded nearby'}</span>
      </div>
    );
  }
  const rows = stopRows(c);
  const anyUnknown = rows.some(r => r.freq === 'frequency unknown');
  return (
    <div className="lm-chip-detail">
      <span className="lm-key-title">{transportTitle(c)}</span>
      {rows.length > 0 ? (
        <PlaceList rows={rows} onFocus={onFocus} focusId={focusId} />
      ) : (
        <>
          {c.names.length > 0 && <NameList names={c.names.slice(0, DETAIL_NAMES)} />}
          <span>Re-run the location step to list these and plot them on the map.</span>
        </>
      )}
      {anyUnknown && <span>Frequency unknown: the published timetables do not cover these stops (they cover England only), so this does not mean there is no service.</span>}
    </div>
  );
}

const VIEW_NOUN: Record<string, string> = {
  'transport-rail-station': 'Railway station in the view',
  'transport-tram-metro': 'Tram or metro stop in the view',
};

/** Full wording, used for the heading of the white panel and the chip's hover text. */
function transportTitle(c: SiteConstraint): string {
  const base = baseOf(c.dataset);
  const d = c.distanceM ?? c.places?.[0]?.distanceM ?? 0;
  if (base !== c.dataset) {
    return base === 'transport-bus-stop' ? `Other bus stops in the view · ${c.count}` : `${VIEW_NOUN[base] ?? c.label}, ${fmtDistance(d)} from the pin`;
  }
  // Tram/metro is searched the same way as bus stops (a walkable cluster, not just the nearest), so it earns
  // the same "N within 800 m" phrasing; rail stays a single nearest station, so keeps the plain distance.
  if (base === 'transport-bus-stop') {
    return c.within800M ? `Bus stops · ${c.within800M} within 800 m` : `Nearest bus stop, ${fmtDistance(d)}`;
  }
  if (base === 'transport-tram-metro') {
    return c.within800M ? `Tram or metro stops · ${c.within800M} within 800 m` : `Nearest tram or metro stop, ${fmtDistance(d)}`;
  }
  return `${TRANSPORT_NOUN[base] ?? c.label}${c.distanceM != null ? `, ${fmtDistance(c.distanceM)}` : ''}`;
}

const TRANSPORT_SHORT: Record<string, string> = {
  'transport-bus-stop': 'Bus Stops',
  'transport-rail-station': 'Rail Stations',
  'transport-tram-metro': 'Metro/Tram Stops',
};

/** Chip wording: just the kind of stop; the distances, counts and frequencies are in the white panel. */
function transportChipText(c: SiteConstraint): string {
  const base = baseOf(c.dataset);
  const name = TRANSPORT_SHORT[base] ?? c.label;
  return base !== c.dataset && base === 'transport-bus-stop' ? `${name} (${c.count})` : name;
}

/** The map points behind each switched-on transport chip. */
export function transportMarkers(constraints: SiteConstraint[], on: string[]): MapMarker[] {
  // Stops found only by zooming out are plotted with their kind's own chip too, so switching Bus on shows every stop in view.
  return constraints.filter(c => (on.includes(c.dataset) || on.includes(baseOf(c.dataset))) && c.status === 'Present').flatMap(c => (isNearbyHeritage(c) ? nearbyRows(c) : stopRows(c)).map(r => r.marker));
}

/** A row of transport chips; switching one on plots its stops or stations on the map and opens its details. */
function TransportChipRow({ list, on, onToggle, label }: { list: SiteConstraint[]; on: string[]; onToggle: (dataset: string, on: boolean) => void; label: string }) {
  return (
    <div className="lm-chips" role="group" aria-label={label}>
      <TransportButtons list={list} on={on} onToggle={onToggle} />
    </div>
  );
}

function TransportButtons({ list, on, onToggle }: { list: SiteConstraint[]; on: string[]; onToggle: (dataset: string, on: boolean) => void }) {
  return (
    <>
      {list.map(c => {
        const swatch = TRANSPORT_COLOUR[baseOf(c.dataset)];
        const absent = c.status === 'Absent';
        const text = absent ? `${TRANSPORT_SHORT[baseOf(c.dataset)] ?? c.label} · none` : transportChipText(c);
        const names = (c.places?.length ? c.places.map(p => p.name) : c.names).slice(0, 4).join('; ');
        const isOn = on.includes(c.dataset);
        const action = `Click to ${isOn ? 'hide' : 'show'} the details${c.places?.length ? ' and the map points' : ''}.`;
        const title = `${transportTitle(c)}${names ? `: ${names}` : ''}. ${action}`;
        return (
          <button key={c.dataset} type="button" className={`lm-chip lm-chip-btn${isOn ? ' lm-chip-on' : ''}`} aria-pressed={isOn} title={title} onClick={() => onToggle(c.dataset, !isOn)}>
            <i style={{ background: swatch }} /><span className="lm-chip-text">{text}</span>
          </button>
        );
      })}
    </>
  );
}

/** How the transport distances and bus frequencies were produced, and their limits. */
export function TransportDisclaimer({ constraints }: { constraints: SiteConstraint[] }) {
  const bus = constraints.find(c => c.dataset === 'transport-bus-stop');
  const timetable = bus?.status === 'Present' ? bus.busService : undefined;
  return (
    <div className="sx-note">
      Straight-line distances from the nearest point of the site outline (or from the pin when no outline has been traced), from NaPTAN (Department for Transport, Open Government Licence).{' '}
      {timetable
        ? 'Bus frequencies are scheduled departures from Bus Open Data Service timetables (Department for Transport, Open Government Licence) for one typical day.'
        : 'NaPTAN records where stops and stations are, not how often they are served, so check timetables before relying on a service.'}
    </div>
  );
}

/** Switched-on overlays and their per-layer hints, shared by the Locate step and the read-only result map. */
export function useLayerState() {
  const [layers, setLayers] = useState<string[]>([]);
  const [layerNotes, setLayerNotes] = useState<Record<string, string>>({});
  const [grades, setGrades] = useState<Record<string, string[] | null>>({});
  function toggleLayer(id: string, on: boolean) {
    setLayers(prev => on ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter(x => x !== id));
  }
  function noteLayer(id: string, note: string | null) {
    setLayerNotes(prev => {
      if ((prev[id] ?? null) === note) return prev;
      const next = { ...prev };
      if (note) next[id] = note; else delete next[id];
      return next;
    });
  }
  function noteGrades(id: string, inView: string[] | null) {
    setGrades(prev => {
      const same = prev[id] === inView || (prev[id] && inView && [...prev[id]!].sort().join('|') === [...inView].sort().join('|'));
      return same ? prev : { ...prev, [id]: inView };
    });
  }
  return { layers, layerNotes, grades, toggleLayer, noteLayer, noteGrades };
}

interface StoredLocation { lat?: number; lon?: number; radiusM?: number; constraints?: SiteConstraint[]; boundary?: { ring?: [number, number][]; parts?: [number, number][][] } }

/** Read-only map of a case's confirmed site, with the same switchable layers as the Locate step. */
export function SiteMapPanel({ siteLocation, caseId, footer, viewFooter }: { siteLocation: string | null | undefined; caseId?: string; footer?: HTMLElement | null; viewFooter?: HTMLElement | null }) {
  const { layers, layerNotes, grades, toggleLayer, noteLayer, noteGrades } = useLayerState();
  const [transportOn, setTransportOn] = useState<string[]>([]);
  const [focus, setFocus] = useState<MapFocus | null>(null);
  let loc: StoredLocation | null = null;
  try { loc = siteLocation ? JSON.parse(siteLocation) as StoredLocation : null; } catch { loc = null; }
  const stored = Array.isArray(loc?.constraints) ? loc.constraints : [];
  const view = useViewDesignations(caseId, loc && typeof loc.lat === 'number' && typeof loc.lon === 'number' ? { lat: loc.lat, lon: loc.lon } : null, loc?.radiusM ?? 250, stored);
  const markers = [...transportMarkers([...stored, ...view.extraTransport], transportOn), ...featureMarkers([...stored, ...view.extra], layers)];
  const markerKey = markers.map(m => m.id).join(',');
  // A focused point whose marker has gone (its chip switched off, or the view moved on) is dropped, which sends the map back.
  useEffect(() => { setFocus(prev => (prev && !markers.some(m => m.id === prev.id) ? null : prev)); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [markerKey]);
  if (!loc || typeof loc.lat !== 'number' || typeof loc.lon !== 'number') return null;
  const inFooter = footer !== undefined;
  const focusOn = (m: MapMarker) => setFocus(prev => (prev?.id === m.id ? null : { ...m, token: (prev?.token ?? 0) + 1 }));
  const toggleTransport = (dataset: string, on: boolean) => setTransportOn(prev => on ? (prev.includes(dataset) ? prev : [...prev, dataset]) : prev.filter(x => x !== dataset));
  const keyFor = (part: 'site' | 'view') => (
    <MapKey
      part={part} active={layers} constraints={stored} view={view} grades={grades} notes={layerNotes}
      transport={{ constraints: part === 'site' ? stored : view.extraTransport, on: transportOn }} onFocus={focusOn} focusId={focus?.id}
    />
  );
  const viewChips = <ViewChips active={layers} onToggle={toggleLayer} view={view} transport={{ on: transportOn, onToggle: toggleTransport }} detail={keyFor('view')} />;
  const viewInOwnSlot = viewFooter !== undefined;
  const notes = (
    <>
      {!viewInOwnSlot && viewChips}
      <MapCredits active={layers} />
    </>
  );
  return (
    <div className="lm-readonly">
      <MapBoundary failMessage="The map could not be loaded.">
        <Suspense fallback={<div className="lm-map lm-map-off"><span className="skel" /></div>}>
          <LocateMap
            position={{ lat: loc.lat, lon: loc.lon }} radiusM={loc.radiusM ?? 250} boundary={Array.isArray(loc.boundary?.ring) ? loc.boundary.ring : null}
            extraRings={Array.isArray(loc.boundary?.parts) && loc.boundary.parts.length ? loc.boundary.parts : undefined} fitToken={0} onPick={() => undefined}
            layers={layers} onLayerNote={noteLayer} readOnly onViewChange={view.onViewChange} onGradesInView={noteGrades} markers={markers} focus={focus} busy={view.loading}
            overlay={<SiteChips constraints={stored} active={layers} onToggle={toggleLayer} transport={{ on: transportOn, onToggle: toggleTransport }} disclaimer={false} />}
          />
        </Suspense>
      </MapBoundary>
      <SiteChips constraints={stored} active={layers} onToggle={toggleLayer} transport={{ on: transportOn, onToggle: toggleTransport }} disclaimer={false} />
      {keyFor('site')}
      {viewInOwnSlot && viewFooter ? createPortal(viewChips, viewFooter) : null}
      {inFooter ? (footer ? createPortal(notes, footer) : null) : <>{viewChips}<MapCredits active={layers} /></>}
    </div>
  );
}
